import { useState } from "react";
import {
  REQUIRED_DOC_TYPES,
  USAGE_FUNCTION_LABELS,
  USAGE_FUNCTIONS,
  type BuildingDto,
  type UsageFunction,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useBuildingCache } from "../building/useBuilding";
import { errMsg } from "./DossierDialogs";
import { useDossierRefresh } from "./useDossier";

export const usageText = (fs: UsageFunction[]) => fs.map((f) => USAGE_FUNCTION_LABELS[f]).join(", ");

/** Gebruiksfuncties als aan/uit-knoppen; een gebouw kan er meer hebben (bijvoorbeeld kantoor met winkel). */
export function UsageFunctionsField({
  value,
  onChange,
}: {
  value: UsageFunction[];
  onChange: (v: UsageFunction[]) => void;
}) {
  const toggle = (f: UsageFunction) =>
    onChange(
      value.includes(f) ? value.filter((x) => x !== f) : USAGE_FUNCTIONS.filter((x) => x === f || value.includes(x)),
    );
  return (
    <fieldset className="usagefield">
      <legend className="label">Gebruiksfunctie</legend>
      <div className="dir-kinds" role="group" aria-label="Gebruiksfunctie">
        {USAGE_FUNCTIONS.map((f) => (
          <button key={f} type="button" className="kpi" aria-pressed={value.includes(f)} onClick={() => toggle(f)}>
            {USAGE_FUNCTION_LABELS[f]}
          </button>
        ))}
      </div>
      <span className="hint">
        Bepaalt welke documenten in het dossier horen
        {value.length > 0 &&
          ` (${[...new Set(value.flatMap((f) => REQUIRED_DOC_TYPES[f]))].length} verplichte soorten)`}
        . Meer dan één kan.
      </span>
    </fieldset>
  );
}

/** Gebruiksfunctie wijzigen vanuit het dossier. */
export function UsageDialog({ building, onClose }: { building: BuildingDto; onClose: () => void }) {
  const toast = useToast();
  const cache = useBuildingCache(building.id);
  const refresh = useDossierRefresh(building.id);
  const [value, setValue] = useState<UsageFunction[]>(building.usageFunctions ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      cache.set(
        await api<BuildingDto>(`/buildings/${building.id}`, { method: "PATCH", body: { usageFunctions: value } }),
      );
      await refresh();
      toast("Gebruiksfunctie opgeslagen");
      onClose();
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Gebruiksfunctie"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy}>
            Opslaan
          </button>
        </>
      }
    >
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <UsageFunctionsField value={value} onChange={setValue} />
    </Modal>
  );
}
